import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useMemo } from 'react';

interface Props {
  /* Used to infer letter(s) for the icon. Typically uses initials */
  identifier: string;
  /* Box size */
  size?: number;
  /* Max number of letters used for initials */
  maxLength?: number;
  /* String used to infer a color. Uses identifier if none given */
  colorSeed?: string;
}

export const GenericIcon = ({ identifier, size = 44, maxLength = 3, colorSeed }: Props): React.JSX.Element => {
  const letters = useMemo(
    () =>
      identifier
        .split(/[._\-\s]/)
        .slice(0, maxLength - 1)
        .map((w) => w.charAt(0).toUpperCase())
        .join(''),
    [identifier],
  );
  const color = useMemo(() => {
    const seed = colorSeed ?? identifier;

    let hash = 5381;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 33) ^ seed.charCodeAt(i);
    }
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 45%, 42%)`;
  }, [colorSeed, identifier]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1.5,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.28 : 0.14),
        fontWeight: 800,
        fontSize: Math.round(size * (letters.length > 1 ? 0.34 : 0.42)),
        letterSpacing: letters.length > 1 ? 0.5 : 0,
        userSelect: 'none',
      }}
    >
      {letters}
    </Box>
  );
};
